import brandIconSrc from '../../assets/images/claima.png';

export const PDF_BRAND = {
  primary: [52, 151, 211],
  primaryDeep: [21, 94, 145],
  navy: [15, 42, 68],
  navyLight: [22, 56, 88],
  accent: [73, 184, 112],
  accentSoft: [232, 247, 239],
  slate: [51, 65, 85],
  slateMuted: [100, 116, 139],
  slateLight: [148, 163, 184],
  paper: [248, 250, 252],
  white: [255, 255, 255],
  border: [226, 232, 240],
  activeText: [21, 128, 61],
  activeBg: [240, 253, 244],
  inactiveText: [100, 116, 139],
  inactiveBg: [241, 245, 249],
};

export const PDF_ORG = {
  name: 'Aman Medicare',
  tagline: 'Healthcare Provider Network',
  reportTitle: 'Facility Directory Export',
  reportSubtitle: 'Provider network listing',
  confidentiality: 'Confidential — For authorized internal use only.',
};

const MARGIN_X = 14;
const FOOTER_H = 16;
const HEADER_TOP = 2;
const HEADER_H = 34;
const BRAND_MUTED = [168, 198, 220];
const BRAND_SUBTLE = [120, 155, 185];

function rgb(c) {
  return c;
}

function formatScopeLabel(meta) {
  if (meta.scope === 'all') return 'All facilities in directory';
  if (meta.search) return `Matching “${meta.search}”`;
  return 'Filtered directory listing';
}

function documentRef(meta) {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const date = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  return `FAC-EXP-${date}-${String(meta.rowCount ?? 0).padStart(4, '0')}`;
}

async function loadBrandIconDataUrl() {
  try {
    const response = await fetch(brandIconSrc);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function drawAccentBar(doc, pageWidth) {
  doc.setFillColor(...rgb(PDF_BRAND.accent));
  doc.rect(0, 0, pageWidth, 0.85, 'F');
  doc.setFillColor(...rgb(PDF_BRAND.primary));
  doc.rect(0, 0.85, pageWidth, 0.55, 'F');
  doc.setFillColor(...rgb(PDF_BRAND.primaryDeep));
  doc.rect(0, 1.4, pageWidth, 0.35, 'F');
}

function drawHeaderBackground(doc, pageWidth, top, height) {
  doc.setFillColor(...rgb(PDF_BRAND.navy));
  doc.rect(0, top, pageWidth, height, 'F');

  doc.setFillColor(...rgb(PDF_BRAND.navyLight));
  doc.triangle(pageWidth - 78, top + height, pageWidth, top + 6, pageWidth, top + height, 'F');

  doc.setFillColor(...rgb(PDF_BRAND.primaryDeep));
  doc.triangle(pageWidth - 46, top + height, pageWidth, top + 14, pageWidth, top + height, 'F');

  doc.setFillColor(...rgb(PDF_BRAND.accent));
  doc.triangle(0, top, 24, top, 0, top + 12, 'F');
  doc.setFillColor(...rgb(PDF_BRAND.navy));
  doc.triangle(0, top, 18, top, 0, top + 9, 'F');
}

function drawBrandBlock(doc, left, top, brandIconDataUrl) {
  const iconSize = 17;
  const iconY = top + (HEADER_H - iconSize) / 2 - 1;

  if (brandIconDataUrl) {
    try {
      doc.addImage(brandIconDataUrl, 'PNG', left, iconY, iconSize, iconSize);
    } catch {
      /* optional */
    }
  }

  const textX = left + (brandIconDataUrl ? iconSize + 5 : 0);
  doc.setTextColor(...rgb(PDF_BRAND.white));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15.5);
  doc.text(PDF_ORG.name, textX, top + 14.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.2);
  doc.setTextColor(...rgb(BRAND_MUTED));
  doc.text(PDF_ORG.tagline, textX, top + 20.5);

  doc.setDrawColor(...rgb(BRAND_SUBTLE));
  doc.setLineWidth(0.15);
  doc.line(textX, top + 23.5, textX + 42, top + 23.5);
}

function drawReportBadge(doc, pageWidth, top) {
  const badgeW = 58;
  const badgeH = 22;
  const badgeX = pageWidth - MARGIN_X - badgeW;
  const badgeY = top + (HEADER_H - badgeH) / 2 - 1;

  doc.setFillColor(...rgb(PDF_BRAND.navyLight));
  doc.setDrawColor(...rgb(BRAND_SUBTLE));
  doc.setLineWidth(0.25);
  doc.roundedRect(badgeX, badgeY, badgeW, badgeH, 2.5, 2.5, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(5.8);
  doc.setTextColor(...rgb(PDF_BRAND.accent));
  doc.text('OFFICIAL REPORT', badgeX + badgeW / 2, badgeY + 5.2, { align: 'center' });

  doc.setTextColor(...rgb(PDF_BRAND.white));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  const titleLines = doc.splitTextToSize(PDF_ORG.reportTitle, badgeW - 8);
  doc.text(titleLines.slice(0, 2), badgeX + badgeW / 2, badgeY + 11.5, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...rgb(BRAND_MUTED));
  doc.text(PDF_ORG.reportSubtitle, badgeX + badgeW / 2, badgeY + badgeH - 3.5, { align: 'center' });
}

/** Full letterhead — page 1 only. Returns Y where table content should start. */
function drawFullLetterhead(doc, meta, brandIconDataUrl) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const left = MARGIN_X;
  const top = HEADER_TOP;

  drawAccentBar(doc, pageWidth);
  drawHeaderBackground(doc, pageWidth, top, HEADER_H);
  drawBrandBlock(doc, left, top, brandIconDataUrl);
  drawReportBadge(doc, pageWidth, top);

  const headerBottom = top + HEADER_H;
  doc.setFillColor(...rgb(PDF_BRAND.accent));
  doc.rect(0, headerBottom, pageWidth, 0.6, 'F');
  doc.setFillColor(...rgb(PDF_BRAND.primary));
  doc.rect(0, headerBottom + 0.6, pageWidth, 0.25, 'F');

  let y = headerBottom + 5;
  const panelTop = y;
  const panelH = 28;
  doc.setFillColor(...rgb(PDF_BRAND.paper));
  doc.setDrawColor(...rgb(PDF_BRAND.border));
  doc.setLineWidth(0.2);
  doc.roundedRect(left, panelTop, pageWidth - left * 2, panelH, 2, 2, 'FD');

  doc.setFillColor(...rgb(PDF_BRAND.primary));
  doc.rect(left, panelTop + 1.5, 2.5, panelH - 3, 'F');

  const generatedAt = meta.generatedAt || new Date().toLocaleString();
  const scope = formatScopeLabel(meta);
  const colCount = meta.columnCount ?? '—';
  const exportedBy = meta.exportedBy || '—';
  const ref = documentRef(meta);

  const col1X = left + 8;
  const col2X = left + (pageWidth - left * 2) / 2 + 4;
  const row1Y = panelTop + 9;
  const row2Y = panelTop + 18;
  const labelColor = PDF_BRAND.slateMuted;
  const valueColor = PDF_BRAND.slate;

  const drawMetaPair = (lx, vy, label, value) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...rgb(labelColor));
    doc.text(label.toUpperCase(), lx, vy);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...rgb(valueColor));
    const maxW = (pageWidth - left * 2) / 2 - 12;
    const lines = doc.splitTextToSize(String(value), maxW);
    doc.text(lines[0] || '—', lx, vy + 4.5);
  };

  drawMetaPair(col1X, row1Y, 'Generated', generatedAt);
  drawMetaPair(col2X, row1Y, 'Scope', scope);
  drawMetaPair(col1X, row2Y, 'Total records', `${(meta.rowCount ?? 0).toLocaleString()} facilities`);
  drawMetaPair(col2X, row2Y, 'Exported by', exportedBy);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...rgb(PDF_BRAND.slateLight));
  doc.text(`Ref ${ref}  ·  ${colCount} columns`, pageWidth - left, panelTop + panelH - 3, { align: 'right' });

  y = panelTop + panelH + 8;

  // Section divider + table intro
  doc.setDrawColor(...rgb(PDF_BRAND.border));
  doc.setLineWidth(0.3);
  doc.line(left, y, pageWidth - left, y);
  y += 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...rgb(PDF_BRAND.navy));
  doc.text('Directory listing', left, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...rgb(PDF_BRAND.slateMuted));
  doc.text(
    `${(meta.rowCount ?? 0).toLocaleString()} entr${meta.rowCount === 1 ? 'y' : 'ies'}`,
    pageWidth - left,
    y,
    { align: 'right' },
  );

  return y + 5;
}

/** Compact running header for continuation pages. */
function drawRunningHeader(doc, pageNumber, totalPages, brandIconDataUrl) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const bandTop = 2;
  const bandH = 13;

  drawAccentBar(doc, pageWidth);

  doc.setFillColor(...rgb(PDF_BRAND.paper));
  doc.rect(0, bandTop, pageWidth, bandH, 'F');

  doc.setFillColor(...rgb(PDF_BRAND.navy));
  doc.rect(0, bandTop + bandH - 0.5, pageWidth, 0.5, 'F');

  const iconSize = 7.5;
  const contentY = bandTop + 8.2;
  if (brandIconDataUrl) {
    try {
      doc.addImage(brandIconDataUrl, 'PNG', MARGIN_X, bandTop + 2.8, iconSize, iconSize);
    } catch {
      /* optional */
    }
  }

  const textX = MARGIN_X + (brandIconDataUrl ? iconSize + 3 : 0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.2);
  doc.setTextColor(...rgb(PDF_BRAND.navy));
  doc.text(`${PDF_ORG.name} · ${PDF_ORG.reportTitle}`, textX, contentY);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...rgb(PDF_BRAND.slateLight));
  doc.text(`Page ${pageNumber} of ${totalPages}`, pageWidth - MARGIN_X, contentY, { align: 'right' });

  return bandTop + bandH + 3;
}

function drawPdfFooter(doc, pageNumber, totalPages, meta) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const footerY = pageHeight - FOOTER_H;

  doc.setFillColor(...rgb(PDF_BRAND.paper));
  doc.rect(0, footerY - 2, pageWidth, FOOTER_H + 2, 'F');

  doc.setDrawColor(...rgb(PDF_BRAND.primary));
  doc.setLineWidth(0.5);
  doc.line(MARGIN_X, footerY, pageWidth - MARGIN_X, footerY);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...rgb(PDF_BRAND.slateMuted));
  doc.text(PDF_ORG.confidentiality, MARGIN_X, footerY + 5);

  doc.setTextColor(...rgb(PDF_BRAND.slateLight));
  doc.text(documentRef(meta), MARGIN_X, footerY + 9.5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...rgb(PDF_BRAND.slate));
  doc.text(`Page ${pageNumber} of ${totalPages}`, pageWidth - MARGIN_X, footerY + 7, { align: 'right' });
}

function buildColumnStyles(headers, baseFontSize) {
  const columnStyles = {};
  headers.forEach((header, idx) => {
    const h = String(header).toLowerCase();
    if (h.includes('phone') || h.includes('email') || h.includes('website')) {
      columnStyles[idx] = { cellWidth: 'wrap', minCellWidth: 24, fontSize: baseFontSize - 0.5 };
    } else if (h.includes('address')) {
      columnStyles[idx] = { cellWidth: 'wrap', minCellWidth: 30 };
    } else if (h.includes('name')) {
      columnStyles[idx] = { cellWidth: 'wrap', minCellWidth: 32, fontStyle: 'bold' };
    } else if (h.includes('code')) {
      columnStyles[idx] = { cellWidth: 20, font: 'courier', fontSize: baseFontSize - 0.5 };
    } else if (h.includes('status')) {
      columnStyles[idx] = { cellWidth: 18, halign: 'center' };
    } else if (h.includes('type') || h.includes('plan')) {
      columnStyles[idx] = { cellWidth: 22 };
    } else if (h.includes('city') || h.includes('state')) {
      columnStyles[idx] = { cellWidth: 20 };
    }
  });
  return columnStyles;
}

function createStatusCellStyler(statusColIdx) {
  return function styleStatusCell(data) {
    if (data.section !== 'body') return;
    if (statusColIdx < 0 || data.column.index !== statusColIdx) return;

    const val = String(data.cell.raw ?? '').toLowerCase();
    if (val === 'active') {
      data.cell.styles.fillColor = PDF_BRAND.activeBg;
      data.cell.styles.textColor = PDF_BRAND.activeText;
      data.cell.styles.fontStyle = 'bold';
    } else if (val === 'inactive') {
      data.cell.styles.fillColor = PDF_BRAND.inactiveBg;
      data.cell.styles.textColor = PDF_BRAND.inactiveText;
    }
  };
}

function timestampSlug() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
}

/**
 * Generate and save a letterheaded facility directory PDF.
 */
export async function renderFacilityExportPdf({ headers, matrix, meta = {}, filename }) {
  const [{ jsPDF }, autoTableModule] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);
  const autoTable = autoTableModule.default;
  const name = filename || `facilities_export_${timestampSlug()}.pdf`;
  const brandIconDataUrl = await loadBrandIconDataUrl();

  const colCount = headers.length;
  const useLandscape = colCount > 4;
  const doc = new jsPDF({
    orientation: useLandscape ? 'landscape' : 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const tableWidth = pageWidth - MARGIN_X * 2;
  const baseFontSize = colCount > 9 ? 6.5 : colCount > 7 ? 7 : colCount > 5 ? 7.5 : 8;

  const displayHeaders = headers.map((h) => String(h).toUpperCase());
  const body = matrix.map((row) => row.map((cell) => String(cell ?? '')));

  const enrichedMeta = {
    ...meta,
    columnCount: headers.length,
  };

  const startY = drawFullLetterhead(doc, enrichedMeta, brandIconDataUrl);
  const statusColIdx = headers.findIndex((h) => String(h).toLowerCase().includes('status'));

  autoTable(doc, {
    head: [displayHeaders],
    body,
    startY,
    tableWidth,
    margin: { left: MARGIN_X, right: MARGIN_X, top: 20, bottom: FOOTER_H + 4 },
    horizontalPageBreak: true,
    horizontalPageBreakRepeat: 0,
    showHead: 'everyPage',
    theme: 'plain',
    styles: {
      fontSize: baseFontSize,
      cellPadding: { top: 2.8, right: 2.5, bottom: 2.8, left: 2.5 },
      overflow: 'linebreak',
      lineColor: PDF_BRAND.border,
      lineWidth: 0.15,
      valign: 'middle',
      textColor: PDF_BRAND.slate,
    },
    headStyles: {
      fillColor: PDF_BRAND.navy,
      textColor: PDF_BRAND.white,
      fontStyle: 'bold',
      fontSize: baseFontSize - 0.5,
      halign: 'left',
      cellPadding: { top: 3.5, right: 2.5, bottom: 3.5, left: 2.5 },
    },
    alternateRowStyles: {
      fillColor: PDF_BRAND.white,
    },
    bodyStyles: {
      fillColor: PDF_BRAND.paper,
    },
    columnStyles: buildColumnStyles(headers, baseFontSize),
    didParseCell: createStatusCellStyler(statusColIdx),
    willDrawPage: (data) => {
      if (data.pageNumber > 1) {
        data.settings.margin.top = 20;
        drawRunningHeader(doc, data.pageNumber, doc.internal.getNumberOfPages(), brandIconDataUrl);
      }
    },
  });

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    drawPdfFooter(doc, i, pageCount, enrichedMeta);
  }

  doc.save(name);
  return { success: true, filename: name };
}
